import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import Toast from '../components/Toast/Toast';
import * as styles from '../components/Toast/index.module.css';

const ToastContext = createContext(undefined);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(
    ({ type = 'info', title, message, duration = 5000 }) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setToasts((prev) => [...prev, { id, type, title, message, duration }]);
      return id;
    },
    [],
  );

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showSuccess = useCallback(
    (title, message, duration) =>
      addToast({ type: 'success', title, message, duration }),
    [addToast],
  );

  const showError = useCallback(
    (title, message, duration) =>
      addToast({ type: 'error', title, message, duration }),
    [addToast],
  );

  const showWarning = useCallback(
    (title, message, duration) =>
      addToast({ type: 'warning', title, message, duration }),
    [addToast],
  );

  const showInfo = useCallback(
    (title, message, duration) =>
      addToast({ type: 'info', title, message, duration }),
    [addToast],
  );

  const contextValue = useMemo(
    () => ({
      addToast,
      removeToast,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [addToast, removeToast, showSuccess, showError, showWarning, showInfo],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {createPortal(
        <div className={styles.toastContainer}>
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              type={toast.type}
              title={toast.title}
              message={toast.message}
              duration={toast.duration}
              onClose={removeToast}
            />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
